import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Divider, Menu, MenuItem, Typography } from '@mui/material';
import { format, parse } from 'date-fns';

import type { Campaign, CampaignStats, ElasticSearchAvailableFilters, PCADisplayType } from 'c-sdk';
import { EsPCAReportField, PCAReportMetric } from 'c-sdk';
import { useEntityData } from 'c-data';
import { usePrevious } from 'react-hanger';

import type { ReportDateSettings, ReportWrapperRefAPI } from 'c-reports/Types';
import { ReportDateTimeframe } from 'c-reports/Types';

import { useUrlState, useUrlStateValue } from 'c-hooks';
import { allFieldValuesEs } from 'c-main/Lib';
import { useCommonTranslation } from 'c-translation';

import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';
import { ReportFieldFilterES } from 'c-main/Components/Campaign/CampaignReports/ReportFieldFilter';

import { PCAMap } from 'c-main/Components/Campaign/CampaignReports/Views';

import type { PCAUrlState, ReportTabProps } from 'c-main/Components/Campaign/CampaignReports/types';
import {
    PCAMetricUrlStateKeys,
    ReportView,
    FieldReportType,
} from 'c-main/Components/Campaign/CampaignReports/types';

import type { PCAViewControlProps } from 'c-main/Components/Campaign/CampaignReports/PCAViewControls';
import { PCAViewControls } from 'c-main/Components/Campaign/CampaignReports/PCAViewControls';

import { NumberFormatWhole } from 'c-components';

import AnalyticsLeaderboardTable from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/EnergyUsage/AnalyticsLeaderboardTable';
import AnalyticsESOverTimeByField from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/EnergyUsage/AnalyticsESOverTimeByField';
import AnalyticsOverallCharts from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/EnergyUsage/AnalyticsOverallCharts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import { useAtomValue } from 'jotai';
import { atom_useLinearReportingOverride } from 'c-main/Pages/Campaign/CampaignOverviewPage/atoms';
import { campaignShowsUnitId, supportsAverageAudience } from 'c-main/Lib';

type Props = { id: number };

const urlDateFormat = 'yyyy-MM-dd';

const AUD_ALL = '__AUD_ALL__';
const AUD_NONE = '__AUD_NONE__';
const DSP_AUDIENCE_FILTER_KEY = 'audience_display_name';
const DSP_NO_AUDIENCE_VALUE = 'nothing';
const DSP_AUDIENCE_MODE_URL_KEY = 'dspAudienceMode';
const DSP_AUDIENCE_MODE_AVERAGES = 'averages';

// The default leaderboard grouping for DSP campaigns (owner + format). Shared
// between the URL-defaults push and the synchronous fallback below so the
// rendered grouping is identical before and after the push lands.
const LEADERBOARD_DEFAULT_FIELDS = [EsPCAReportField.DisplayOwner, EsPCAReportField.DisplayFormat];

const toStringArray = (v: unknown): string[] => {
    if (Array.isArray(v)) return v.filter(x => x != null).map(x => String(x));
    if (v == null) return [];
    if (typeof v === 'object') return Object.keys(v as Record<string, unknown>);
    return [String(v)];
};

const uiAudienceValueFromFilter = (aud?: string[]) => {
    if (!aud || aud.length === 0) return AUD_ALL;

    const v = aud[0];

    if (v === DSP_NO_AUDIENCE_VALUE) return AUD_NONE;

    return String(v);
};

const filterAudienceValueFromUi = (ui: string): string[] => {
    if (ui === AUD_ALL) return [];
    if (ui === AUD_NONE) return [DSP_NO_AUDIENCE_VALUE];
    return [ui];
};

const MetricTotalsHeader: React.FC<{
    impressions: number;
    plays: number;
}> = ({ impressions, plays }) => {
    const t = useCommonTranslation();
    const averageImpressionMultiplier = plays > 0 ? impressions / plays : 0;

    return (
        <Box sx={{ borderColor: 'grey.300' }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    textAlign: 'center',
                    alignItems: 'center',
                }}
            >
                <TotalBlock
                    value={<NumberFormatWhole value={impressions} />}
                    label={t('Modules.Main.Campaigns.Overview.dsp.dspImpressions')}
                />
                <TotalBlock
                    value={<NumberFormatWhole value={plays} />}
                    label={t('Modules.Main.Campaigns.Overview.Analytics.totalPlays')}
                />
                <TotalBlock
                    value={averageImpressionMultiplier.toFixed(2)}
                    label={t('Modules.Main.Campaigns.Overview.dsp.averageDSPIm')}
                />
            </Box>
        </Box>
    );
};

const TotalBlock: React.FC<{ value: React.ReactNode; label: string }> = ({ value, label }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography
            variant="h2"
            sx={{
                fontWeight: 600,
                lineHeight: 1.1,
                color: 'grey.600',
            }}
        >
            {value}
        </Typography>
        <Typography variant="body1" sx={{ color: 'grey.400', mt: 0.5 }}>
            {label}
        </Typography>
    </Box>
);

const CampaignAnalyticsTab: React.FC<Props> = ({ id }) => {
    const t = useCommonTranslation();
    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id });
    const [audAnchorEl, setAudAnchorEl] = useState<null | HTMLElement>(null);
    const audMenuOpen = Boolean(audAnchorEl);

    const campaignHasDspReporting = useMemo(() => {
        const t = campaign?.type;
        return t != null && t !== 'linear' && t !== 'default';
    }, [campaign?.type]);
    const useLinearReportingOverride = useAtomValue(atom_useLinearReportingOverride);
    const isDsp = campaignHasDspReporting && !useLinearReportingOverride;

    const {
        campaignStart,
        campaignEnd,
        urlDateValue,

        advancedFilters,
        showAdvancedFilters,
        advancedFiltersCount,

        view,
        field,
        fields,
        fieldSearchValue,

        reportType,
        byFieldReportType,

        countFields,

        byFieldAccumulative,
        setByFieldAccumulative,

        onSwitchField,
        onSwitchFields,
        onSwitchView,
        onSearchTermUpdated,
        onReportTypeSwitched,
        onByFieldReportTypeSwitched,
        onAdvancedFiltersUpdated,
        onResetAdvancedFilters,

        environmentMode,
        source,

        notices,
        dataStatusFilter,
        setDataStatusFilter,
        urlEsSelfRefiningFiltersValue,
    } = useCampaignWrapperState(id);

    const rawUrlFieldValue = useUrlStateValue<string | null>(PCAMetricUrlStateKeys.field, null);
    // read straight from the URL rather than using `view` from the wrapper state - that mirrors
    // the URL in an effect, so it is a render behind while a view switch is in flight
    const rawUrlViewValue = useUrlStateValue<ReportView>(
        PCAMetricUrlStateKeys.view,
        ReportView.Field,
    );
    const urlType = useUrlStateValue<'digital' | 'classic' | null>('type', null);
    const availableTypes = useMemo<ReadonlyArray<PCADisplayType>>(
        () => (environmentMode ? campaign?.pca_display_types?.[environmentMode] ?? [] : []),
        [campaign?.pca_display_types, environmentMode],
    );
    const hasKnownDisplayTypesForEnvironment = useMemo(
        () =>
            Boolean(
                environmentMode &&
                    campaign?.pca_display_types &&
                    Object.prototype.hasOwnProperty.call(
                        campaign.pca_display_types,
                        environmentMode,
                    ),
            ),
        [campaign?.pca_display_types, environmentMode],
    );
    const hasDigitalRaw = availableTypes.includes('Display');
    const hasClassic = availableTypes.includes('Classic');
    const hasDigital =
        hasDigitalRaw || (hasKnownDisplayTypesForEnvironment && availableTypes.length === 0);
    const resolvedType = useMemo<'digital' | 'classic' | null>(() => {
        if (urlType === 'digital' && hasDigital) return 'digital';
        if (urlType === 'classic' && hasClassic) return 'classic';
        if (hasDigital) return 'digital';
        if (hasClassic) return 'classic';

        return null;
    }, [hasClassic, hasDigital, urlType]);

    const isGeopathOnly = useMemo(() => {
        if (!isDsp) return false;
        const selected = (advancedFilters?.[DSP_AUDIENCE_FILTER_KEY] ?? []).filter(
            v => v && v !== '',
        );
        if (selected.length !== 1) return false;
        const v = selected[0].toLowerCase();
        return v.includes('geopath') || v.includes('route');
    }, [advancedFilters, isDsp]);

    const showRoute = useMemo(
        () => campaign?.type === 'vistar_uk' || campaign?.type === 'hivestack_uk',
        [campaign?.type],
    );

    const isOverTime = byFieldReportType === FieldReportType.OverTime;
    const isOverall = byFieldReportType === FieldReportType.Overall;

    const [overTimeMetric, setOverTimeMetric] = useState<PCAReportMetric>(PCAReportMetric.Impacts);
    const [overallMetric, setOverallMetric] = useState<PCAReportMetric>(PCAReportMetric.Impacts);
    const actualOverTimeMetric = isGeopathOnly ? PCAReportMetric.Impacts : overTimeMetric;

    useEffect(() => {
        if (isGeopathOnly) {
            setOverTimeMetric(PCAReportMetric.Impacts);
            setOverallMetric(PCAReportMetric.Impacts);
        }
    }, [isGeopathOnly]);

    const { push, pushMany, replaceMany } = useUrlState();
    const dspAudienceMode =
        useUrlStateValue<string | null>(DSP_AUDIENCE_MODE_URL_KEY, null) ?? null;
    const isAverageAudienceSupported = useMemo(
        () => supportsAverageAudience({ type: campaign?.type }),
        [campaign?.type],
    );
    const isAverageAudienceMode = useMemo(
        () => isDsp && isAverageAudienceSupported && dspAudienceMode === DSP_AUDIENCE_MODE_AVERAGES,
        [dspAudienceMode, isAverageAudienceSupported, isDsp],
    );
    const hasSelectedDspAudienceFilter = useMemo(
        () =>
            Array.isArray(advancedFilters?.[DSP_AUDIENCE_FILTER_KEY]) &&
            advancedFilters[DSP_AUDIENCE_FILTER_KEY].some(v => v != null && v !== ''),
        [advancedFilters],
    );
    const hasSelectedAuditedDspAudienceFilter = useMemo(
        () =>
            Array.isArray(advancedFilters?.[DSP_AUDIENCE_FILTER_KEY]) &&
            advancedFilters[DSP_AUDIENCE_FILTER_KEY].some(
                v => v != null && v !== '' && v !== DSP_NO_AUDIENCE_VALUE,
            ),
        [advancedFilters],
    );
    const prevHasSelectedDspAudienceFilter = usePrevious(hasSelectedDspAudienceFilter);
    const reportWrapperRef = useRef<ReportWrapperRefAPI>();

    const fieldFilters = useMemo<Record<string, string[]>>(() => {
        const cloned: Record<string, string[]> = Object.fromEntries(
            Object.entries(advancedFilters ?? {}).map(([k, v]) => [
                k,
                Array.isArray(v) ? [...v] : [],
            ]),
        );
        if (resolvedType && !isDsp)
            cloned.display_type = [resolvedType === 'digital' ? 'Display' : 'Classic'];
        if (isAverageAudienceMode) {
            if (cloned[DSP_AUDIENCE_FILTER_KEY]?.includes(DSP_NO_AUDIENCE_VALUE)) {
                cloned[DSP_AUDIENCE_FILTER_KEY] = [DSP_NO_AUDIENCE_VALUE];
            } else {
                delete cloned[DSP_AUDIENCE_FILTER_KEY];
            }
            delete cloned.audience_version;
        }
        return cloned;
    }, [advancedFilters, isAverageAudienceMode, isDsp, resolvedType]);

    useEffect(() => {
        if ((isDsp && isAverageAudienceSupported) || dspAudienceMode == null) return;
        replaceMany({ [DSP_AUDIENCE_MODE_URL_KEY]: null });
    }, [dspAudienceMode, isAverageAudienceSupported, isDsp, replaceMany]);

    useEffect(() => {
        if (!isAverageAudienceMode || !hasSelectedAuditedDspAudienceFilter) return;
        if (prevHasSelectedDspAudienceFilter !== false) return;
        replaceMany({ [DSP_AUDIENCE_MODE_URL_KEY]: null });
    }, [
        hasSelectedAuditedDspAudienceFilter,
        isAverageAudienceMode,
        prevHasSelectedDspAudienceFilter,
        replaceMany,
    ]);

    useEffect(() => {
        if (!isDsp) return;
        // map view has no group by field, so don't default one in - doing so would push the
        // view back to field and make the map unreachable
        if (rawUrlViewValue === ReportView.Map) return;
        if (rawUrlFieldValue != null) return;
        if (byFieldReportType !== FieldReportType.OverallLeaderboard) return;

        replaceMany({
            [PCAMetricUrlStateKeys.field]: EsPCAReportField.DisplayOwner,
            [PCAMetricUrlStateKeys.fields]: LEADERBOARD_DEFAULT_FIELDS.join(','),
            [PCAMetricUrlStateKeys.view]: ReportView.Field,
            [PCAMetricUrlStateKeys.byFieldReportType]: FieldReportType.OverallLeaderboard,
        });
    }, [byFieldReportType, isDsp, replaceMany, rawUrlFieldValue, rawUrlViewValue]);

    const initialStartDate = useMemo(() => {
        if (urlDateValue.startDate == null) return new Date(campaignStart);
        return parse(urlDateValue.startDate, urlDateFormat, new Date());
    }, [urlDateValue.startDate, campaignStart]);

    const initialEndDate = useMemo(() => {
        if (urlDateValue.endDate == null) return new Date(campaignEnd);
        return parse(urlDateValue.endDate, urlDateFormat, new Date());
    }, [urlDateValue.endDate, campaignEnd]);

    const prevTimeframeVal = usePrevious(urlDateValue.timeframe);
    useEffect(() => {
        if (prevTimeframeVal != undefined && prevTimeframeVal !== urlDateValue.timeframe) {
            reportWrapperRef?.current?.setDates(
                urlDateValue.timeframe,
                initialStartDate,
                initialEndDate,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlDateValue]);

    const prevCountFields = usePrevious(countFields);
    const prevAdvancedFilters = usePrevious(advancedFilters);
    useEffect(() => {
        if (prevCountFields != undefined && prevAdvancedFilters != undefined) {
            reportWrapperRef?.current?.refreshData?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countFields, advancedFilters]);

    const [rawEsFilters, setRawEsFilters] = useState<ElasticSearchAvailableFilters>(
        {} as ElasticSearchAvailableFilters,
    );

    const esFilters = useMemo(
        () => allFieldValuesEs(rawEsFilters, urlEsSelfRefiningFiltersValue),
        [rawEsFilters, urlEsSelfRefiningFiltersValue],
    );

    const advancedFilterEs = useMemo(
        () =>
            esFilters ? (
                <ReportFieldFilterES
                    allValuesByField={esFilters}
                    selectedFields={advancedFilters}
                    onSearch={onAdvancedFiltersUpdated}
                    onReset={onResetAdvancedFilters}
                />
            ) : null,
        [esFilters, advancedFilters, onAdvancedFiltersUpdated, onResetAdvancedFilters],
    );

    const dateSettings = useMemo<ReportDateSettings>(
        () => ({
            minDate: campaignStart,
            maxDate: campaignEnd,
            availableTimeframes: [
                ReportDateTimeframe.Daily,
                ReportDateTimeframe.Weekly,
                ReportDateTimeframe.Monthly,
                ReportDateTimeframe.Range,
            ],
            timeframe: urlDateValue.timeframe,
            onDatesUpdated: (timeframe, start, end) => {
                push(PCAMetricUrlStateKeys.date, {
                    timeframe,
                    startDate:
                        start && timeframe !== ReportDateTimeframe.All
                            ? format(start, urlDateFormat)
                            : undefined,
                    endDate:
                        end && timeframe !== ReportDateTimeframe.All
                            ? format(end, urlDateFormat)
                            : undefined,
                } as PCAUrlState['date']);
            },
        }),
        [campaignEnd, campaignStart, urlDateValue.timeframe, push],
    );

    const [campaignStats, setCampaignStats] = useState<CampaignStats>(null);
    const [esAvailableFilters, setEsAvailableFilters] = useState<ElasticSearchAvailableFilters>(
        {} as any,
    );
    const onReportResponse: ReportTabProps['onResponse'] = useCallback(
        ({
            campaignStats,
            filters,
        }: {
            campaignStats?: CampaignStats;
            filters?: ElasticSearchAvailableFilters;
        }) => {
            if (campaignStats) setCampaignStats(campaignStats);
            if (filters) setEsAvailableFilters(filters);
        },
        [],
    );
    const headerMetric = PCAReportMetric.Impacts;

    const audienceVersionOptions = useMemo(() => {
        const raw = toStringArray(
            (esAvailableFilters as any)?.[DSP_AUDIENCE_FILTER_KEY] ??
                (esAvailableFilters as any)?.audience_version,
        );

        const hasNoAudience = raw.includes(DSP_NO_AUDIENCE_VALUE);

        const versions = Array.from(new Set(raw.filter(v => v !== DSP_NO_AUDIENCE_VALUE)));

        return { hasNoAudience, versions };
    }, [esAvailableFilters]);

    const audienceUiValue = useMemo(
        () => uiAudienceValueFromFilter(advancedFilters?.[DSP_AUDIENCE_FILTER_KEY]),
        [advancedFilters],
    );
    const audienceLabel = useMemo(() => {
        if (isAverageAudienceMode) {
            return t('Modules.Main.Campaigns.Overview.Analytics.audienceAverages');
        }
        if (audienceUiValue === AUD_ALL)
            return t('Modules.Main.Campaigns.Overview.Analytics.audienceAll');
        if (audienceUiValue === AUD_NONE)
            return t('Modules.Main.Campaigns.Overview.Analytics.audienceNone');
        return String(audienceUiValue);
    }, [audienceUiValue, isAverageAudienceMode, t]);
    const comparisonSeriesLabel = useMemo(() => {
        if (isAverageAudienceMode) {
            return t('Modules.Main.Campaigns.Overview.Analytics.dspAverageImpacts');
        }
        if (isGeopathOnly && audienceUiValue !== AUD_ALL && audienceUiValue !== AUD_NONE) {
            return String(audienceUiValue);
        }
        return t('Modules.Main.Campaigns.Overview.Analytics.geopathImpressionsLabel');
    }, [audienceUiValue, isAverageAudienceMode, isGeopathOnly, t]);

    const viewControlsProps = useMemo<PCAViewControlProps>(
        () => ({
            searchValue: fieldSearchValue,
            onSearchValueUpdated: onSearchTermUpdated,
            field,
            fields,
            type: reportType,
            onTypeChange: onReportTypeSwitched,
            metric: headerMetric,
            onFieldSwitch: onSwitchField,
            onFieldsSwitch: onSwitchFields,
            view,
            onViewChange: onSwitchView,
            showAdvancedFilters,
            filtersCount: advancedFiltersCount,
            onDataStatusChange: setDataStatusFilter,
            dataStatus: dataStatusFilter,
            notices,
            isDsp,
            showUnitId: campaignShowsUnitId(campaign),
            extraControls: (
                <>
                    <Button
                        variant="contained"
                        endIcon={<ExpandMoreIcon />}
                        startIcon={<GroupsIcon />}
                        onClick={e => setAudAnchorEl(e.currentTarget)}
                        sx={{ borderRadius: 999, textTransform: 'none', px: 2 }}
                    >
                        {audienceLabel}
                    </Button>

                    <Menu
                        anchorEl={audAnchorEl}
                        open={audMenuOpen}
                        onClose={() => setAudAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                        <MenuItem
                            onClick={() => {
                                onAdvancedFiltersUpdated(
                                    {
                                        ...(advancedFilters ?? {}),
                                        [DSP_AUDIENCE_FILTER_KEY]: [] as any,
                                    },
                                    countFields,
                                );
                                push(DSP_AUDIENCE_MODE_URL_KEY, null);
                                setAudAnchorEl(null);
                            }}
                        >
                            {t('Modules.Main.Campaigns.Overview.Analytics.audienceAll')}
                        </MenuItem>

                        {isAverageAudienceSupported && (
                            <MenuItem
                                onClick={() => {
                                    onAdvancedFiltersUpdated(
                                        {
                                            ...(advancedFilters ?? {}),
                                            [DSP_AUDIENCE_FILTER_KEY]: [] as any,
                                        },
                                        countFields,
                                    );
                                    push(DSP_AUDIENCE_MODE_URL_KEY, DSP_AUDIENCE_MODE_AVERAGES);
                                    setAudAnchorEl(null);
                                }}
                            >
                                {t('Modules.Main.Campaigns.Overview.Analytics.audienceAverages')}
                            </MenuItem>
                        )}

                        {audienceVersionOptions.hasNoAudience && (
                            <MenuItem
                                onClick={() => {
                                    onAdvancedFiltersUpdated(
                                        {
                                            ...(advancedFilters ?? {}),
                                            [DSP_AUDIENCE_FILTER_KEY]: [
                                                DSP_NO_AUDIENCE_VALUE,
                                            ] as any,
                                        },
                                        countFields,
                                    );
                                    push(DSP_AUDIENCE_MODE_URL_KEY, null);
                                    setAudAnchorEl(null);
                                }}
                            >
                                {t('Modules.Main.Campaigns.Overview.Analytics.audienceNone')}
                            </MenuItem>
                        )}

                        {audienceVersionOptions.versions.map(v => (
                            <MenuItem
                                key={v}
                                onClick={() => {
                                    onAdvancedFiltersUpdated(
                                        {
                                            ...(advancedFilters ?? {}),
                                            [DSP_AUDIENCE_FILTER_KEY]: [v] as any,
                                        },
                                        countFields,
                                    );
                                    push(DSP_AUDIENCE_MODE_URL_KEY, null);
                                    setAudAnchorEl(null);
                                }}
                            >
                                {v}
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            ),
        }),
        [
            fieldSearchValue,
            onSearchTermUpdated,
            field,
            fields,
            reportType,
            onReportTypeSwitched,
            headerMetric,
            onSwitchField,
            onSwitchFields,
            view,
            onSwitchView,
            showAdvancedFilters,
            advancedFiltersCount,
            setDataStatusFilter,
            dataStatusFilter,
            notices,
            isDsp,
            campaign,
            audienceLabel,
            audAnchorEl,
            audMenuOpen,
            t,
            audienceVersionOptions.hasNoAudience,
            audienceVersionOptions.versions,
            onAdvancedFiltersUpdated,
            advancedFilters,
            countFields,
            push,
            isAverageAudienceSupported,
        ],
    );

    const additionalControls = useMemo(
        () => (
            <PCAViewControls
                {...viewControlsProps}
                field={view === ReportView.Overall ? undefined : field}
                type={byFieldReportType}
                onTypeChange={onByFieldReportTypeSwitched}
            />
        ),
        [byFieldReportType, field, onByFieldReportTypeSwitched, view, viewControlsProps],
    );

    // On a DSP campaign's first visit with no field params in the URL, the
    // effect above (guarded by `rawUrlFieldValue == null`) pushes the default
    // leaderboard grouping into the URL *after* mount. Left alone, the
    // leaderboard would fetch once with the fallback single-field grouping, then
    // blank (`setPcaData(null)`) and refetch when `fields` settles — the brief
    // data-then-empty flash seen only on first visit. To avoid it we resolve the
    // same default grouping synchronously below and pass it straight to the
    // leaderboard, so the rendered grouping is identical before and after the
    // push lands — the push becomes a no-op for what's shown (no flash, no
    // wasted fetch). This flag marks exactly that pre-push window.
    const leaderboardDefaultsPending =
        isDsp &&
        rawUrlFieldValue == null &&
        byFieldReportType === FieldReportType.OverallLeaderboard;

    const tableOrOverTime = useMemo(() => {
        if (isOverTime) {
            return (
                <AnalyticsESOverTimeByField
                    campaignId={id}
                    environment={environmentMode}
                    source={source}
                    metric={actualOverTimeMetric}
                    onMetricChange={setOverTimeMetric}
                    field={view === ReportView.Overall ? undefined : (field as any)}
                    fields={view === ReportView.Overall ? undefined : fields}
                    currentStartDate={initialStartDate}
                    currentEndDate={initialEndDate}
                    timeFrame={urlDateValue.timeframe}
                    dateSettings={dateSettings}
                    fieldFilters={fieldFilters}
                    geopathOnly={isGeopathOnly}
                    onFiltersUpdated={setRawEsFilters}
                    groupResultsByDate={view === ReportView.Overall}
                    additionalControls={additionalControls}
                    advancedFilter={advancedFilterEs}
                    showAdvancedFilters={showAdvancedFilters.value}
                    onResponse={onReportResponse}
                    audienceMode={isAverageAudienceMode ? 'averages' : 'standard'}
                    comparisonLabel={comparisonSeriesLabel}
                    comparisonPrimary={isGeopathOnly || isAverageAudienceMode}
                    foldBadKeysToOther={isDsp}
                />
            );
        }
        if (isOverall) {
            return (
                <AnalyticsOverallCharts
                    campaignId={id}
                    environment={environmentMode}
                    source={source}
                    field={field as any}
                    fields={fields}
                    metric={isGeopathOnly ? PCAReportMetric.Impacts : overallMetric}
                    onMetricChange={setOverallMetric}
                    currentStartDate={initialStartDate}
                    currentEndDate={initialEndDate}
                    currentTimeframe={urlDateValue.timeframe}
                    dateSettings={dateSettings}
                    fieldFilters={fieldFilters}
                    refineFilters={urlEsSelfRefiningFiltersValue}
                    geopathOnly={isGeopathOnly}
                    onFiltersUpdated={setRawEsFilters}
                    additionalControls={additionalControls}
                    advancedFilter={advancedFilterEs}
                    showAdvancedFilters={showAdvancedFilters.value}
                    onResponse={onReportResponse}
                    audienceMode={isAverageAudienceMode ? 'averages' : 'standard'}
                    comparisonLabel={comparisonSeriesLabel}
                    comparisonPrimary={isGeopathOnly || isAverageAudienceMode}
                    foldBadKeysToOther={isDsp}
                />
            );
        }

        return (
            <AnalyticsLeaderboardTable
                campaignId={id}
                field={leaderboardDefaultsPending ? EsPCAReportField.DisplayOwner : (field as any)}
                fields={leaderboardDefaultsPending ? LEADERBOARD_DEFAULT_FIELDS : fields}
                environment={environmentMode}
                source={source}
                showRoute={showRoute}
                currentStartDate={initialStartDate}
                currentEndDate={initialEndDate}
                currentTimeframe={urlDateValue.timeframe}
                dateSettings={dateSettings}
                reportWrapperRef={reportWrapperRef as any}
                fieldFilters={fieldFilters}
                refineFilters={urlEsSelfRefiningFiltersValue}
                geopathOnly={isGeopathOnly}
                onFiltersUpdated={setRawEsFilters}
                onResponse={onReportResponse}
                additionalControls={additionalControls}
                advancedFilter={advancedFilterEs}
                showAdvancedFilters={showAdvancedFilters.value}
                advancedFilters={advancedFilters}
                audienceMode={isAverageAudienceMode ? 'averages' : 'standard'}
                averagesAvailable={isAverageAudienceSupported}
                foldBadKeysToOther={isDsp}
            />
        );
    }, [
        isOverTime,
        isOverall,
        id,
        field,
        fields,
        environmentMode,
        source,
        initialStartDate,
        initialEndDate,
        urlDateValue.timeframe,
        dateSettings,
        fieldFilters,
        urlEsSelfRefiningFiltersValue,
        isGeopathOnly,
        onReportResponse,
        additionalControls,
        advancedFilterEs,
        showAdvancedFilters.value,
        actualOverTimeMetric,
        view,
        overallMetric,
        showRoute,
        advancedFilters,
        isAverageAudienceMode,
        isAverageAudienceSupported,
        comparisonSeriesLabel,
        leaderboardDefaultsPending,
        isDsp,
    ]);

    if (resolvedType == null) return <Box />;

    return (
        <Box pb={2} pt={1}>
            {campaignStats != null && (
                <MetricTotalsHeader
                    impressions={campaignStats?.impacts ?? 0}
                    plays={campaignStats?.plays ?? 0}
                />
            )}

            <Divider sx={{ my: 2, borderBottomWidth: 2, mb: 1 }} />

            {campaign != null && (
                <Box>
                    {(view === ReportView.Overall || view === ReportView.Field) && tableOrOverTime}

                    {view === ReportView.Map && (
                        <PCAMap
                            onResponse={onReportResponse}
                            campaign={campaign}
                            campaignStart={campaignStart}
                            campaignEnd={campaignEnd}
                            currentStartDate={initialStartDate}
                            currentEndDate={initialEndDate}
                            currentTimeframe={urlDateValue.timeframe}
                            metric={headerMetric}
                            dateSettings={dateSettings}
                            advancedFilter={advancedFilterEs}
                            showAdvancedFilters={showAdvancedFilters.value}
                            countFields={countFields}
                            fieldFilters={fieldFilters}
                            environment={environmentMode}
                            source={source}
                            additionalControls={additionalControls}
                            onEsFiltersUpdated={setRawEsFilters}
                            setOverTimeAccumulative={setByFieldAccumulative}
                            overTimeAccumulative={byFieldAccumulative}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default CampaignAnalyticsTab;
